import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Injector,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { EventClickArg } from '@fullcalendar/core';
import { DateClickArg } from '@fullcalendar/interaction';
import { TimezoneService } from '@sc/data-access-configuration';
import {
  DossierHttpService,
  InsightService,
} from '@sc/data-access-http-services';
import {
  AgendaEvent,
  Appointment,
  Dossier,
  DossierComments,
  lang,
} from '@sc/data-access-models';
import { StorageService } from '@sc/data-access-storage';
import { TranslatorService } from '@sc/data-access-translation';
import { AuthService } from '@sc/feature-auth-okta';
import { DialogService } from '@sc/feature-dialog';
import {
  DossierCustomMoveInfos,
  DossierManagerService,
  EventExtendedProps,
  FittersManagerService,
  ResourceExtendedProps,
  ResourcesManagerService,
} from '@sc/feature-fitter-shared-libs';
import { LoadingService } from '@sc/feature-loader';
import { SnackbarService } from '@sc/feature-snackbar';
import { dayjs, getPeriodReplacementVehicleUse } from '@sc/utils';
import { EMPTY, Observable, of } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { BaseAbstractDirective } from '../../base-abstract';
import {
  AppointmentsValidationInBasketDialogComponent,
  NotifyAfterDragCalendarDialogComponent,
} from '../../dialogs';
import {
  PlanningFormUpdateDossierEvent,
  PlanningPanelsStates,
} from '../../models';
import {
  DossierLightUpdateError,
  DossierLightUpdateErrorInstance,
  DossierLightUpdateErrorType,
  DossierLightUpdateResult,
} from '../../models/planning-dossier-update-errors';
import {
  PlanningCalendarService,
  PlanningSandboxService,
} from '../../services';
import { PlanningFormComponent } from '../planning-form';
import { PlanningCalendarComponent } from '../planning-calendar';
import { PlanningFormEventComponent } from '../planning-form-event';
import { PlanningBasketComponent } from '../planning-basket';
import { NgIf, AsyncPipe } from '@angular/common';
import { PlanningFiltersComponent } from '../planning-filters';
import { MatSidenavModule } from '@angular/material/sidenav';
import { FitterNameDisplayService } from '@sc/feature-contextual-display';

@Component({
  selector: 'sc-planning-page',
  styleUrls: ['./planning.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `<mat-sidenav-container>
    <mat-sidenav
      [(opened)]="planningPanelsStates.isLeftSideNavOpened"
      mode="side"
      (openedChange)="onSideNavToggle()"
    >
      <sc-planning-filters [fitters]="fitters$ | async"></sc-planning-filters>
    </mat-sidenav>
    <mat-sidenav
      [(opened)]="planningPanelsStates.isRightSideNavOpened"
      mode="side"
      position="end"
      (openedChange)="onSideNavToggle()"
    >
      <sc-planning-basket
        *ngIf="!planningPanelsStates.isCustomEventFormOpened"
        [externalDossiers]="externalDossiersToValidate$ | async"
        [requestedApmtDossiers]="externalAppointmentRequests$ | async"
        [fitters]="fitters$ | async"
        [calendarDay]="calendarDay"
        (validateExternalDossierEvent)="onValidateExternalDossier($event)"
        (saveExternalDossierEvent)="onSaveExternalDossier($event)"
        (saveExternalDossierByDayEvent)="onSaveExternalDossierByDay($event)"
      ></sc-planning-basket>
      <sc-planning-event-form
        *ngIf="planningPanelsStates.isCustomEventFormOpened"
        [_event]="planningCellSelected"
        [_fitters]="fitters$ | async"
        (closeEvent)="onCloseRightSideNavClick()"
        (validateEvent)="onValidateEvent($event)"
      ></sc-planning-event-form>
    </mat-sidenav>

    <sc-planning-calendar
      [class.isAppointmentFormVisible]="
        planningPanelsStates.isAppointmentFormOpened
      "
      [class.isAppointmentFormReduced]="
        planningPanelsStates.isAppointmentFormReduced
      "
      [events]="events$ | async"
      [fitters]="fitters$ | async"
      [resources]="resources$ | async"
      [panelsStates]="planningPanelsStates"
      [basketEventDay]="basketEventDay"
      (toggleFiltersPanel)="toggleFiltersPanel()"
      (toggleBasketPanel)="toggleBasketPanel()"
      (handleNewEventClickEvent)="handleNewCustomEventClick()"
      (handleDateClickEvent)="handleCalendarDateClick($event)"
      (handleMoveEvent)="handleCalendarMoveEvent($event)"
      (handleEventClickEvent)="handleCalendarEventClick($event)"
      (handleNewInterventionClickEvent)="handleCalendarInterventionClick()"
      (handleNewExceptionalClosingClickEvent)="
        handleNewExceptionalClosingClick()
      "
      (handleUnavailabilityClickEvent)="handleUnavailabilityClickEvent()"
      (changeCalendarDay)="changeCalendarDay($event)"
    ></sc-planning-calendar>
    <sc-planning-form
      [class.isAppointmentFormReduced]="
        planningPanelsStates.isAppointmentFormReduced
      "
      *ngIf="planningPanelsStates.isAppointmentFormOpened"
      [fitters]="fitters$ | async"
      [_dossier]="planningCellSelected"
      [mustSaveToPrint]="planningPanelsStates.mustSaveToPrint"
      [isFormOpen]="!planningPanelsStates.isAppointmentFormReduced"
      [errors]="planningPanelsStates.formErrors"
      (toggleDetailsEvent)="toggleDetailsClick()"
      (createEvent)="onCreateDossier($event)"
      (bookRequestEvent)="onBookRequestedApmt($event)"
      (updateEvent)="onUpdateDossier($event)"
      (abandonDossier)="onAbandonDossier($event)"
      (closeEvent)="onCloseDossier()"
      (createDuplicateEvent)="onCreateDuplicateDossier($event)"
      (saveDraft)="onSaveDraft($event)"
      (saveLightDossier)="onLightUpdateDossier($event)"
      (printDossier)="onPrintDossierClick($event)"
      (saveChatComments)="onSaveChatComments($event)"
    ></sc-planning-form>
  </mat-sidenav-container>`,
  imports: [
    MatSidenavModule,
    PlanningFiltersComponent,
    NgIf,
    PlanningBasketComponent,
    PlanningFormEventComponent,
    PlanningCalendarComponent,
    PlanningFormComponent,
    AsyncPipe,
  ],
  providers: [PlanningSandboxService, FitterNameDisplayService],
})
export class PlanningComponent
  extends BaseAbstractDirective
  implements OnInit, OnDestroy
{
  events$ = this.planningSb.events$;
  externalDossiersToValidate$ =
    this.planningSb.shownExternalDossiersToValidate$;
  externalAppointmentRequests$ =
    this.planningSb.shownExternalAppointmentRequests$;
  fitters$ = this.planningSb.fitters$;
  resources$ = this.planningSb.resources$;

  planningPanelsStates: PlanningPanelsStates = {
    isLeftSideNavOpened: null,
    isRightSideNavOpened: null,
    isAppointmentFormOpened: null,
    isAppointmentFormReduced: null,
    isCustomEventFormOpened: null,
    formErrors: null,
    mustSaveToPrint: null,
  };

  planningPanelsStatesBeforeFormOpened: PlanningPanelsStates;

  planningCellSelected: any;

  calendarDay: string = dayjs().format();
  basketEventDay: string | Date;

  detailledDisplay: boolean;

  constructor(
    injector: Injector,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly insightService: InsightService,
    private readonly planningCalendarService: PlanningCalendarService,
    private readonly planningSb: PlanningSandboxService,
    private readonly resManagerService: ResourcesManagerService,
    private readonly routeService: ActivatedRoute,
    private readonly routerService: Router,
    private readonly loadingService: LoadingService,
    private readonly snackbarService: SnackbarService,
    private readonly translator: TranslatorService,
    public dialogService: DialogService,
    private readonly authService: AuthService,
    private readonly dossierHttpService: DossierHttpService,
    private readonly storage: StorageService,
    private readonly timezoneService: TimezoneService,
    private readonly fittersManagerService: FittersManagerService,
    private readonly dossierManagerService: DossierManagerService
  ) {
    super(injector);
    this.insightService.trackPageView({
      name: 'Planning page',
    });
  }

  ngOnInit(): void {
    this.snackbarService.setFocusManagement(true, {
      documentQuery: 'sc-planning-page sc-planning-calendar',
    });

    this.planningSb.initPlanning();

    this.detailledDisplay = this.storage.getItem(
      'calendar-appointment-default-view'
    );

    this.authService.isAuthenticated$().subscribe((isauth) => {
      this.planningSb.setIsAuth(isauth);
    });

    this.routeService.queryParams
      .pipe(
        switchMap((queries) => {
          if (queries.dossier) {
            return this.planningSb.fetchDossier(queries.dossier);
          } else {
            return of(null);
          }
        }),
        switchMap((dossier) => {
          return this.fittersManagerService
            .getOrFetchCurrentFitters()
            .pipe(map((res) => ({ dossier, fitters: res })));
        })
      )
      .subscribe(
        (response) => {
          if (response.dossier) {
            if (
              response.fitters
                ?.map((fitter) => fitter?.id)
                ?.includes(
                  response?.dossier?.appointment?.fitterId ||
                    response?.dossier?.requestAppointment?.fitterId
                ) ||
              (response.dossier?.appointment?.status === 'DRAFT' &&
                response.dossier?.creationData?.email ===
                  this.authService.getAccountLogin())
            ) {
              this.planningCellSelected = response.dossier;
              this.openAppointmentForm();
            }
          }
          this.changeDetectorRef.markForCheck();
        },
        (error: HttpErrorResponse) => {
          this.snackbarService.showError(
            this.translator.getInstantTranslation('Snackbar.errorTitle'),
            this.translator.getInstantTranslation(
              error?.error?.message || 'Planning.initFetchDossierError'
            )
          );

          console.error(error);
        }
      );
    this.planningSb.getLastPingObservable().subscribe((lastPing) => {
      console.log(
        'last ping ' +
          lastPing.ok +
          ' ' +
          dayjs(lastPing.date).format('HH:mm:ss')
      );
    });
  }

  ngOnDestroy(): void {
    this.snackbarService.setFocusManagement(false);
  }

  triggerCalendarRerender() {
    this.planningSb.updatePlanning();
    this.planningCalendarService.waitForNextCalendarResize().subscribe(() => {
      this.changeDetectorRef.markForCheck();
      this.planningSb.askToRerenderPlanning();
    });
  }

  forceFormEditMode(applyForce = true) {
    if (applyForce) {
      this.routerService.navigate([], {
        relativeTo: this.routeService,
        queryParams: { forceEdit: '1' },
        queryParamsHandling: 'merge',
      });
    } else {
      this.routerService.navigate([], {
        relativeTo: this.routeService,
        queryParams: {},
      });
    }
  }

  onSideNavToggle() {
    this.planningSb.askToRerenderPlanning();
  }

  toggleFiltersPanel() {
    this.planningPanelsStates.isLeftSideNavOpened =
      !this.planningPanelsStates.isLeftSideNavOpened;
    this.changeDetectorRef.markForCheck();
  }

  toggleBasketPanel() {
    this.planningPanelsStates.isRightSideNavOpened =
      !this.planningPanelsStates.isRightSideNavOpened;

    if (!this.planningPanelsStates.isRightSideNavOpened) {
      this.forceFormEditMode(false);

      this.planningPanelsStates.isAppointmentFormOpened = false;
      this.planningPanelsStates.isAppointmentFormReduced = false;
      this.planningPanelsStates.isCustomEventFormOpened = false;
    }

    this.changeDetectorRef.markForCheck();
  }

  onCloseRightSideNavClick() {
    this.forceFormEditMode(false);
    this.closeCustomEventForm();
    this.changeDetectorRef.markForCheck();
  }

  /**
   * Click on empty space in calendar
   * @param infos FullCalendar event
   */
  handleCalendarDateClick(infos: DateClickArg) {
    const extendedProps = infos?.resource
      ?.extendedProps as ResourceExtendedProps;

    if (!this.resManagerService.isAnyRVRes(extendedProps?.type)) {
      this.fitters$.pipe(take(1)).subscribe((fitters) => {
        const fitter = fitters.find((f) => f.id === extendedProps?.fitterId);

        if (!fitter) {
          return;
        }
        const time = this.planningCalendarService.computeTimeOffsetOffCalendar(
          infos.dateStr,
          fitter.timezone
        );

        this.planningCellSelected = {
          id: null,
          appointment: {
            isReplacementVehicleSelected: false,
            outsideIntervention: this.resManagerService.isAnyOutsideRes(
              extendedProps.type
            ),
            fitterId: extendedProps?.fitterId,
            // Need to shift the time saved in the object with the timezone
            time: time,
            endTime: dayjs(time)
              .add(1, 'hour')
              .tz(extendedProps.timezone)
              .format(),
            timezone: extendedProps.timezone,
          },
        };

        // Add the technician to the appointment if we clicked on a technician column
        if (extendedProps?.type === 'TECHNICIAN') {
          const techName = this.resManagerService.getResourceTechnician(
            extendedProps?.resId
          );
          if (!this.resManagerService.isDefaultTechnician(techName)) {
            this.planningCellSelected.appointment.assignedResources = [
              techName,
            ];
          }
        }
        // Add the interventionVehicle to the appointment if we clicked on an interventionVehicle column
        if (extendedProps?.type === 'INTERVENTION_VEHICLE') {
          const interventionVehicleName =
            this.resManagerService.getResourceInterventionVehicle(
              extendedProps?.resId
            );
          if (
            !this.resManagerService.isDefaultInterventionVehicle(
              interventionVehicleName
            )
          ) {
            this.planningCellSelected.appointment.assignedInterventionVehicle =
              interventionVehicleName;
          }
        }

        this.forceFormEditMode(false);
        this.openAppointmentForm();
      });
    }
  }

  /**
   * Click on the "Intervention" button to create a completely empty dossier
   */
  handleCalendarInterventionClick() {
    const thisHour = dayjs().utc().minute(0).second(0).millisecond(0);
    this.planningCellSelected = {
      id: null,
      appointment: {
        time: thisHour.add(1, 'hour').format(),
        endTime: thisHour.add(2, 'hour').format(),
      },
    };

    this.forceFormEditMode(false);
    this.openAppointmentForm();
  }

  /**
   * Click on occupied space in calendar
   * @param props Full calendar event
   */
  handleCalendarEventClick(props: EventClickArg) {
    const extendedProps = props.event.extendedProps as EventExtendedProps;

    if (this.resManagerService.isDossierEventExtendedProps(extendedProps)) {
      this.planningCellSelected = extendedProps.dossier;
      this.openAppointmentForm();
    } else {
      this.planningCellSelected = {
        ...extendedProps.event,
        startTime: extendedProps.event.startTime,
        endTime: extendedProps.event.endTime,
      };
      this.openCustomEventForm();
    }
  }

  /**
   * Handling resize and move event
   * custom/moved property allow overwrite of hours
   * @param param0
   */
  handleCalendarMoveEvent({
    dossier,
    startTime,
    endTime,
    timezone,
    newResource,
    oldResource,
    resizeResource,
  }: {
    dossier: Dossier;
    startTime: string | Date;
    endTime: string | Date;
    timezone: string;
    newResource?: ResourceExtendedProps;
    oldResource?: ResourceExtendedProps;
    resizeResource?: ResourceExtendedProps;
  }) {
    this.fitters$
      .pipe(
        take(1),
        switchMap((fitters) => {
          const adjustedStartTime =
            this.planningCalendarService.computeTimeOffsetOffCalendar(
              startTime,
              timezone
            );
          const adjustedEndTime =
            this.planningCalendarService.computeTimeOffsetOffCalendar(
              endTime,
              timezone
            );

          const customDossierInfos: DossierCustomMoveInfos = {
            time: adjustedStartTime,
            endTime: adjustedEndTime,
            timezone,
            newResource: newResource || null,
            oldResource: oldResource || null,
            resizeResource: resizeResource || null,
          };

          // Do nothing if the event change is not authorized
          if (!this.canMoveOrResizeEvent(customDossierInfos)) {
            return EMPTY;
          }

          const dossierAfterMove =
            this.resManagerService.generateDossierAfterMove(
              dossier,
              customDossierInfos,
              fitters
            );

          this.planningCellSelected = {
            ...dossier,
            custom: customDossierInfos,
          };

          return this.canUpdateDossierWithoutForm(
            dossier,
            dossierAfterMove,
            customDossierInfos
          ).pipe(
            map((canUpdateWithoutForm) => ({
              canUpdateWithoutForm: canUpdateWithoutForm.canUpdate,
              dossierAfterMove,
              customDossierInfos,
              errors:
                (canUpdateWithoutForm as DossierLightUpdateError).errors || [],
            }))
          );
        })
      )
      .subscribe((updateInfos) => {
        if (updateInfos.canUpdateWithoutForm) {
          this.onUpdateDossier({
            dossier: updateInfos.dossierAfterMove,
            keepFormOpen: false,
            isLocal: false,
          });

          if (
            this.mustCreatePreviousAppointment(
              dossier?.appointment,
              updateInfos.dossierAfterMove?.appointment
            )
          ) {
            if (
              dayjs().isBefore(updateInfos.dossierAfterMove?.appointment?.time)
            ) {
              // We can notify only if the new appointment is in the future
              this.dialogService
                .showDialog(NotifyAfterDragCalendarDialogComponent, {})
                .pipe(
                  filter((result) => result === 'notify'),
                  switchMap(() => {
                    this.loadingService.setLoading(true);
                    return this.planningSb.notifyAppointment({
                      dossierId: updateInfos.dossierAfterMove?.id,
                      notifType: 'MODIFICATION',
                    });
                  })
                )
                .subscribe(
                  () => {
                    this.loadingService.setLoading(false);
                    this.snackbarService.showSuccess(
                      this.translator.getInstantTranslation(
                        'PlanningForm.sendNotifSuccess'
                      )
                    );
                  },
                  (error: HttpErrorResponse) => {
                    this.loadingService.setLoading(false);
                    this.snackbarService.showError(
                      this.translator.getInstantTranslation(
                        'Snackbar.errorTitle'
                      ),
                      this.translator.getInstantTranslation(
                        error?.error?.message || 'PlanningForm.sendNotifError'
                      )
                    );

                    console.error(error);
                  }
                );
            }
          }
        } else {
          this.forceFormEditMode();
          this.openAppointmentForm(true, updateInfos.errors);
        }
      });
  }

  private mustCreatePreviousAppointment(
    appointment: Appointment,
    appointmentAfterMove: Appointment
  ): boolean {
    return (
      !dayjs(appointment?.time).isSame(appointmentAfterMove?.time) ||
      !dayjs(appointment?.endTime).isSame(appointmentAfterMove?.endTime) ||
      appointment?.fitterId !== appointmentAfterMove?.fitterId
    );
  }

  handleNewCustomEventClick() {
    this.planningCellSelected = {
      type: 'GENERIC',
      title: null,
    };
    this.openCustomEventForm();
  }

  handleNewExceptionalClosingClick() {
    const fitterId = this.planningSb?.getMonoCenterSelectedFitter();
    this.planningCellSelected = {
      type: 'SPECIAL_CLOSING',
      title: null,
      fitterId,
    };
    this.openCustomEventForm();
  }

  handleUnavailabilityClickEvent() {
    this.planningCellSelected = {
      type: 'TECH_LEAVE',
      title: null,
    };
    this.openCustomEventForm();
  }

  changeCalendarDay(newDay: Date) {
    this.calendarDay = newDay.toISOString();
  }

  onValidateExternalDossier(dossier: Dossier) {
    this.planningCellSelected = dossier;
    if (dossier.appointment?.time) {
      this.basketEventDay = dossier.appointment.time;
    }
    this.forceFormEditMode();

    if (this.detailledDisplay) {
      this.openAppointmentForm();
    } else {
      this.openAppointmentFormReduced();
    }
  }

  toggleDetailsClick() {
    this.planningPanelsStates.isAppointmentFormReduced =
      !this.planningPanelsStates.isAppointmentFormReduced;
    this.changeDetectorRef.markForCheck();
    this.triggerCalendarRerender();
  }

  onValidateEvent(events: AgendaEvent[]) {
    this.loadingService.setLoading(true);
    if (events?.length > 1) {
      this.subs.bulkCreateEvent = this.planningSb
        .bulkCreateEvent(events)
        .subscribe(
          () => {
            this.planningSb.updatePlanning();
            this.closeCustomEventForm();
          },
          (error: HttpErrorResponse) => {
            this.loadingService.setLoading(false);
            this.snackbarService.showError(
              this.translator.getInstantTranslation('Snackbar.errorTitle'),
              this.translator.getInstantTranslation(
                error?.error?.message || 'Planning.eventCreationError'
              )
            );

            console.error(error);
          }
        );
    } else {
      if (events[0].id) {
        this.subs.updateEvent = this.planningSb
          .updateEvent(events[0])
          .subscribe(
            () => {
              this.planningSb.updatePlanning();
              this.closeCustomEventForm();
            },
            (error: HttpErrorResponse) => {
              this.loadingService.setLoading(false);
              this.snackbarService.showError(
                this.translator.getInstantTranslation('Snackbar.errorTitle'),
                this.translator.getInstantTranslation(
                  error?.error?.message || 'Planning.eventUpdateError'
                )
              );

              console.error(error);
            }
          );
      } else {
        this.subs.createEvent = this.planningSb
          .createEvent(events[0])
          .subscribe(
            () => {
              this.planningSb.updatePlanning();
              this.closeCustomEventForm();
            },
            (error: HttpErrorResponse) => {
              this.loadingService.setLoading(false);
              this.snackbarService.showError(
                this.translator.getInstantTranslation('Snackbar.errorTitle'),
                this.translator.getInstantTranslation(
                  error?.error?.message || 'Planning.eventCreationError'
                )
              );

              console.error(error);
            }
          );
      }
    }
  }

  onSaveExternalDossier(dossier: Dossier) {
    this.dialogService
      .showDialog(
        AppointmentsValidationInBasketDialogComponent,
        {
          title: this.translator.getInstantTranslation(
            'PlanningBasketAppointment.dialog.title'
          ),
          text: this.translator.getInstantTranslation(
            'PlanningBasketAppointment.dialog.text'
          ),
        },
        'dialog-close-btn-outside-container'
      )
      .subscribe((result: any) => {
        if (result !== 'validate') {
          return;
        }

        const dossierUpdate: Dossier = {
          ...dossier,
          appointment: {
            ...dossier.appointment,
            isValidatedByFitter: true,
          },
          claim: {
            ...dossier.claim,
            documents: dossier.claim.documents ?? [],
          },
        };
        this.onUpdateDossier({
          dossier: dossierUpdate,
          keepFormOpen: false,
          isLocal: false,
        });
        this.basketEventDay = dossierUpdate.appointment.time;
      });
  }

  onSaveExternalDossierByDay(dossiers: Dossier[]) {
    this.dialogService
      .showDialog(
        AppointmentsValidationInBasketDialogComponent,
        {
          title: this.translator.getInstantTranslation(
            'PlanningBasketAppointment.dialog.titleByDay'
          ),
          text: this.translator.getInstantTranslation(
            'PlanningBasketAppointment.dialog.textByDay'
          ),
        },
        'dialog-close-btn-outside-container'
      )
      .subscribe((result: any) => {
        if (result !== 'validate') {
          return;
        }

        const dossiersValidated = [];
        let canUpdateAfterValidationAlreadyExist = true;
        let changeDate = true;
        const date = dossiers[0].appointment.time;
        dossiers?.forEach((dossier) => {
          const canUpdateAlreadyExist =
            !this.planningSb.isAppointmentAlreadyExist(dossier);
          const canUpdateFormValid =
            this.planningSb.canValidateDossier(dossier);
          if (dossiersValidated.length > 0) {
            canUpdateAfterValidationAlreadyExist =
              !this.planningSb.isAppointmentAlreadyExist(
                dossier,
                dossiersValidated
              );
          }

          if (
            canUpdateAlreadyExist &&
            canUpdateAfterValidationAlreadyExist &&
            canUpdateFormValid
          ) {
            const dossierUpdate: Dossier = {
              ...dossier,
              appointment: {
                ...dossier.appointment,
                isValidatedByFitter: true,
              },
              claim: {
                ...dossier.claim,
                documents: dossier.claim.documents ?? [],
              },
            };
            dossiersValidated.push(dossier);
            this.onUpdateDossier({
              dossier: dossierUpdate,
              keepFormOpen: false,
              isLocal: false,
            });
          } else if (!canUpdateFormValid) {
            this.showAppointmentIncompleteForm();
          } else {
            this.showAppointmentAlreadyExist();
          }
          if (!dayjs(dossier.appointment.time).isSame(date, 'day')) {
            changeDate = false;
          }
        });
        if (changeDate) {
          this.basketEventDay = date;
        }
      });
  }

  onCreateDossier(dossier: Dossier) {
    this.loadingService.setLoading(true);
    this.planningSb.createAgendaDossier(dossier, false).subscribe(
      (result) => {
        this.planningSb.updatePlanningDossierChange(result.dossier);
        this.closeAppointmentForm();

        this.snackbarService.showSuccess(
          dossier?.state === 'ABANDONED'
            ? this.translator.getInstantTranslation(
                'Planning.dossierAbandonSuccess'
              )
            : this.translator.getInstantTranslation(
                'Planning.dossierSaveSuccess'
              )
        );
      },
      (error: HttpErrorResponse) => {
        this.loadingService.setLoading(false);
        this.snackbarService.showError(
          this.translator.getInstantTranslation('Snackbar.errorTitle'),
          dossier?.state === 'ABANDONED'
            ? this.translator.getInstantTranslation(
                error?.error?.message || 'Planning.dossierAbandonError'
              )
            : this.translator.getInstantTranslation(
                error?.error?.message || 'Planning.dossierCreationError'
              )
        );

        console.error(error);
      }
    );
  }

  onCreateDuplicateDossier(dossier: Dossier) {
    this.loadingService.setLoading(true);

    let resultObs: Observable<any>;
    if (dossier.id?.length > 0 && !this.planningSb.isDossierDraft(dossier)) {
      resultObs = this.planningSb.updateAgendaDossier({
        ...dossier,
        appointment: { ...dossier.appointment, isValidatedByFitter: true },
      });
    } else {
      resultObs = this.planningSb.createAgendaDossier(dossier);
    }

    const copyAttributes = <T>(obj?: T, defaultVal?: T): T =>
      obj ? { ...obj } : defaultVal;
    const copyList = <T>(list?: T[], defaultVal: T[] = []): T[] => [
      ...(list || defaultVal),
    ];

    resultObs.subscribe(
      () => {
        this.snackbarService.showSuccess(
          this.translator.getInstantTranslation('Planning.dossierSaveSuccess')
        );
        const duplicateDossier: Dossier = {
          creationData: copyAttributes(dossier?.creationData),
          customer: copyAttributes(dossier?.customer),
          claim: {
            ...dossier.claim,
            glass: null,
            operationType: null,
            calibration: null,
          },
          appointment: {
            ...dossier.appointment,
            id: null,
            time: dossier?.appointment?.endTime,
            endTime: null,
            immobilisationDuration: null,
            interventionDuration: null,
            status: null,
            reminderIsSent: null,
            partAvailability: 'UNAVAILABLE',
            partProvider: null,
            interventionId: null,

            assignedInterventionVehicle: null,
            assignedReplacementVehicle: null,
            assignedReplacementVehicleUse: null,
          },
          requestAppointment: copyAttributes(dossier?.requestAppointment),
          previousAppointments: copyList(dossier?.previousAppointments),
          suggestedAppointments: copyList(dossier?.suggestedAppointments),
          insurer: copyAttributes(dossier?.insurer),
          insurerData: copyAttributes(dossier?.insurerData),
          policyInformation: copyAttributes(dossier?.policyInformation),
          gpdr: copyAttributes(dossier?.gpdr),
          fitterInfo: copyAttributes(dossier?.fitterInfo),
          call: copyList(dossier?.call),
          searchCriteria: copyAttributes(dossier?.searchCriteria),
          selectedLanguage: dossier?.selectedLanguage,
          custom: copyAttributes(dossier?.custom),
        };
        this.planningCellSelected = duplicateDossier;
        this.loadingService.setLoading(false);
        this.changeDetectorRef.detectChanges();
      },
      (error: HttpErrorResponse) => {
        this.loadingService.setLoading(false);
        this.snackbarService.showError(
          this.translator.getInstantTranslation('Snackbar.errorTitle'),
          this.translator.getInstantTranslation(
            error?.error?.message || 'Planning.dossierCreationError'
          )
        );

        console.error(error);
      }
    );
  }

  onBookRequestedApmt(dossier: Dossier) {
    this.loadingService.setLoading(true);

    this.planningSb.bookRequestedAppointment(dossier).subscribe({
      next: () => {
        this.loadingService.setLoading(false);
        this.closeAppointmentForm();
        this.snackbarService.showSuccess(
          this.translator.getInstantTranslation('Planning.dossierSaveSuccess')
        );
      },
      error: (error: HttpErrorResponse) => {
        this.loadingService.setLoading(false);
        this.snackbarService.showError(
          this.translator.getInstantTranslation('Snackbar.errorTitle'),
          this.translator.getInstantTranslation(
            error?.error?.message || 'Planning.apmtRequestValidationError'
          )
        );

        console.error(error);
      },
    });
  }

  onUpdateDossier(
    updatedDossierEvent: PlanningFormUpdateDossierEvent,
    shouldPrint = false
  ) {
    this.loadingService.setLoading(true);
    let resultDossierObs: Observable<Dossier>;
    if (updatedDossierEvent.isLocal) {
      resultDossierObs = of(updatedDossierEvent.dossier);
    } else {
      resultDossierObs = this.planningSb
        .updateAgendaDossier({
          ...updatedDossierEvent.dossier,
          appointment: {
            ...updatedDossierEvent.dossier.appointment,
            isValidatedByFitter: true,
          },
        })
        .pipe(map((result) => result.dossier));
    }

    resultDossierObs.subscribe(
      (dossier) => {
        this.planningCellSelected = dossier;
        this.planningSb.updatePlanningDossierChange(dossier);
        if (!updatedDossierEvent.isLocal) {
          this.snackbarService.showSuccess(
            dossier?.state === 'ABANDONED'
              ? this.translator.getInstantTranslation(
                  'Planning.draftDeleteSuccess'
                )
              : dossier?.state === 'CANCELLED'
              ? this.translator.getInstantTranslation(
                  'Planning.dossierCancelSuccess'
                )
              : this.translator.getInstantTranslation(
                  'Planning.dossierSaveSuccess'
                )
          );
        }
        if (!updatedDossierEvent.keepFormOpen) {
          this.closeAppointmentForm();
        }
        if (updatedDossierEvent.isResumed) {
          this.planningSb
            .notifyAppointment({
              dossierId: dossier?.id,
              notifType: 'BOOK',
            })
            .subscribe({
              next: () => {
                this.loadingService.setLoading(false);
              },
              error: (error: HttpErrorResponse) => {
                this.loadingService.setLoading(false);
                this.snackbarService.showError(
                  this.translator.getInstantTranslation('Snackbar.errorTitle'),
                  this.translator.getInstantTranslation(
                    error?.error?.message || 'Planning.bookNotificationError'
                  )
                );

                console.error(error);
              },
            });
        }
        if (shouldPrint) {
          this.printDossier(dossier.id);
        }
      },
      (error) => {
        this.loadingService.setLoading(false);
        if (!updatedDossierEvent.isLocal) {
          this.snackbarService.showError(
            this.translator.getInstantTranslation('Snackbar.errorTitle'),
            updatedDossierEvent?.dossier?.state === 'ABANDONED'
              ? this.translator.getInstantTranslation(
                  error?.error?.message || 'Planning.draftDeleteError'
                )
              : updatedDossierEvent?.dossier?.state === 'CANCELLED'
              ? this.translator.getInstantTranslation(
                  'Planning.dossierCancelError'
                )
              : this.translator.getInstantTranslation(
                  error?.error?.message || 'Planning.dossierUpdateError'
                )
          );
        }

        console.error(error);
      }
    );
  }

  onAbandonDossier(updatedDossierEvent: PlanningFormUpdateDossierEvent) {
    this.loadingService.setLoading(true);
    let resultDossierObs: Observable<Dossier>;
    const dossierToUpdate = {
      ...updatedDossierEvent.dossier,
      appointment: {
        ...updatedDossierEvent.dossier.appointment,
        isValidatedByFitter: true,
      },
    };
    if (updatedDossierEvent.isLocal) {
      resultDossierObs = of(dossierToUpdate);
    } else {
      resultDossierObs = this.planningSb
        .abandonDossier(dossierToUpdate)
        .pipe(
          map((result) =>
            result?.abandonned ? updatedDossierEvent?.dossier : null
          )
        );
    }

    resultDossierObs.subscribe({
      next: (dossier) => {
        this.planningSb.updatePlanningDossierChange(dossier);
        if (!updatedDossierEvent.isLocal) {
          this.snackbarService.showSuccess(
            this.translator.getInstantTranslation(
              'Planning.dossierAbandonSuccess'
            )
          );
        }
        if (!updatedDossierEvent.keepFormOpen) {
          this.closeAppointmentForm();
        }
      },
      error: (error) => {
        this.loadingService.setLoading(false);
        if (!updatedDossierEvent.isLocal) {
          this.snackbarService.showError(
            this.translator.getInstantTranslation('Snackbar.errorTitle'),
            this.translator.getInstantTranslation(
              error?.error?.message || 'Planning.dossierAbandonSuccess'
            )
          );
        }

        console.error(error);
      },
    });
  }

  onPrintDossierClick(param: { dossier: Dossier; wasModified: boolean }) {
    if (
      param.wasModified &&
      param.dossier.id &&
      param.dossier?.appointment?.id &&
      !!param.dossier?.appointment?.status &&
      param.dossier?.appointment?.status !== 'CANCELLED' &&
      param.dossier?.appointment?.status !== 'NO_SHOW' &&
      param.dossier?.appointment?.isValidatedByFitter &&
      param?.dossier?.state !== 'ABANDONED'
    ) {
      this.onUpdateDossier(
        {
          dossier: param.dossier,
          keepFormOpen: true,
          isLocal: false,
          isResumed: false,
        },
        true
      );
    } else if (
      param.wasModified &&
      !param.dossier?.appointment?.isValidatedByFitter &&
      param.dossier?.appointment?.status !== 'DRAFT' &&
      param?.dossier?.state !== 'ABANDONED' &&
      param.dossier?.appointment?.status !== 'CANCELLED' &&
      param.dossier?.appointment?.status !== 'NO_SHOW'
    ) {
      this.loadingService.setLoading(true);
      this.planningSb.onLightUpdateDossier(param.dossier).subscribe({
        next: (result) => {
          this.loadingService.setLoading(false);
          this.planningCellSelected = result.dossier;
          this.printDossier(result?.dossier.id);
        },
        error: (error: HttpErrorResponse) => {
          this.loadingService.setLoading(false);
          this.snackbarService.showError(
            this.translator.getInstantTranslation('Snackbar.errorTitle'),
            this.translator.getInstantTranslation(
              error?.error?.message || 'Planning.bookNotificationError'
            )
          );
          console.error(error);
        },
      });
    } else if (
      param.wasModified &&
      param.dossier?.appointment?.status === 'DRAFT'
    ) {
      this.planningSb?.saveDraftDossier(param.dossier)?.subscribe({
        next: (dossier) => {
          this.planningCellSelected = dossier;
          this.planningSb.updatePlanningDossierChange(dossier as Dossier);

          this.printDossier(param.dossier.id);
          this.changeDetectorRef.markForCheck();
          this.loadingService.setLoading(false);
        },
        error: (error: HttpErrorResponse) => {
          this.loadingService.setLoading(false);
          this.snackbarService.showError(
            this.translator.getInstantTranslation('Snackbar.errorTitle'),
            this.translator.getInstantTranslation(
              error?.error?.message || 'Planning.bookNotificationError'
            )
          );
          console.error(error);
        },
      });
    } else {
      this.printDossier(param.dossier.id);
    }
  }

  printDossier(dossierId: string) {
    const lang =
      (this.planningCalendarService.getStorageLanguage() as lang) ??
      this.translator.getCurrentLang().value;
    this.dossierHttpService.getPDFDossier(dossierId, lang).subscribe({
      next: (res) => {
        const pdfFile = new Blob([res as any], {
          type: 'application/pdf',
        });
        const pdfUrl = URL.createObjectURL(pdfFile);
        const printWindow = window.open(pdfUrl);
        printWindow.print();
      },
      error: (error: HttpErrorResponse) => {
        this.snackbarService.showError(
          this.translator.getInstantTranslation('Snackbar.errorTitle'),
          this.translator.getInstantTranslation(
            error?.error?.message || 'PlanningForm.fetchDossierError'
          )
        );

        console.error(error);
      },
    });
  }

  onLightUpdateDossier(dossierToUpdate: Dossier) {
    this.loadingService.setLoading(true);
    return this.planningSb.onLightUpdateDossier(dossierToUpdate).subscribe({
      next: (dossier) => {
        this.planningCellSelected = dossier?.dossier;
        this.loadingService.setLoading(false);
        this.snackbarService.showSuccess(
          this.translator.getInstantTranslation('Planning.dossierSaveSuccess')
        );
      },
      error: (error: HttpErrorResponse) => {
        this.loadingService.setLoading(false);
        this.snackbarService.showError(
          this.translator.getInstantTranslation('Snackbar.errorTitle'),
          this.translator.getInstantTranslation(
            error?.error?.message || 'Planning.bookNotificationError'
          )
        );

        console.error(error);
      },
    });
  }

  onCloseDossier() {
    this.closeAppointmentForm();
  }

  openAppointmentForm(
    mustSaveToPrint = false,
    errors?: DossierLightUpdateErrorInstance[]
  ) {
    this.planningPanelsStatesBeforeFormOpened = {
      ...this.planningPanelsStates,
    };
    this.planningPanelsStates = {
      isAppointmentFormOpened: true,
      isAppointmentFormReduced: false,
      isCustomEventFormOpened: false,
      isRightSideNavOpened: false,
      isLeftSideNavOpened: false,
      formErrors: errors,
      mustSaveToPrint: mustSaveToPrint,
    };
    this.changeDetectorRef.markForCheck();

    //this.triggerCalendarRerender();
  }

  closeAppointmentForm() {
    this.forceFormEditMode(false);

    this.planningPanelsStates = {
      isAppointmentFormOpened: false,
      isAppointmentFormReduced: false,
      isCustomEventFormOpened: false,
      isRightSideNavOpened:
        !!this.planningPanelsStatesBeforeFormOpened?.isRightSideNavOpened,
      isLeftSideNavOpened:
        !!this.planningPanelsStatesBeforeFormOpened?.isLeftSideNavOpened,
      formErrors: null,
      mustSaveToPrint: false,
    };

    this.triggerCalendarRerender();
  }

  openCustomEventForm() {
    this.planningPanelsStatesBeforeFormOpened = {
      ...this.planningPanelsStates,
    };
    this.planningPanelsStates = {
      isAppointmentFormOpened: false,
      isAppointmentFormReduced: false,
      isCustomEventFormOpened: true,
      isRightSideNavOpened: true,
      isLeftSideNavOpened: false,
      formErrors: null,
      mustSaveToPrint: false,
    };
    this.changeDetectorRef.markForCheck();

    this.triggerCalendarRerender();
  }

  closeCustomEventForm() {
    this.planningPanelsStates = {
      isAppointmentFormOpened: false,
      isAppointmentFormReduced: false,
      isCustomEventFormOpened: false,
      isRightSideNavOpened:
        this.planningPanelsStatesBeforeFormOpened.isRightSideNavOpened,
      isLeftSideNavOpened:
        this.planningPanelsStatesBeforeFormOpened.isLeftSideNavOpened,
      formErrors: null,
      mustSaveToPrint: false,
    };

    this.triggerCalendarRerender();
  }

  canMoveOrResizeEvent(dossierMoveInfos: DossierCustomMoveInfos) {
    const isMoveEvent =
      dossierMoveInfos.newResource && dossierMoveInfos.oldResource;

    if (isMoveEvent) {
      const isSameFitterMove =
        dossierMoveInfos.newResource.fitterId ===
        dossierMoveInfos.oldResource.fitterId;

      // Block moves of replacement vehicle events between fitters
      if (
        this.resManagerService.isAnyRVRes(dossierMoveInfos.oldResource.type) &&
        !isSameFitterMove
      ) {
        return false;
      }

      // Block moves from and to replacement vehicle if the other column is not also a replacement vehicle column
      if (
        (this.resManagerService.isAnyRVRes(dossierMoveInfos.oldResource.type) &&
          !this.resManagerService.isAnyRVRes(
            dossierMoveInfos.newResource.type
          )) ||
        (!this.resManagerService.isAnyRVRes(
          dossierMoveInfos.oldResource.type
        ) &&
          this.resManagerService.isAnyRVRes(dossierMoveInfos.newResource.type))
      ) {
        return false;
      }
    }

    return true;
  }

  canUpdateDossierWithoutForm(
    dossierBeforeMove: Dossier,
    dossier: Dossier,
    customDossierInfos: DossierCustomMoveInfos
  ): Observable<DossierLightUpdateResult> {
    return this.planningSb.fetchDossier(dossier.id).pipe(
      switchMap((dossierFetched) =>
        this.fitters$.pipe(
          take(1),
          map((fitters) => [dossierFetched, fitters] as const)
        )
      ),
      map(([dossierFetched, fitters]): DossierLightUpdateResult => {
        const foundErrors: DossierLightUpdateErrorInstance[] = [];

        // check if someone else is already on the dossier
        if (
          this.dossierManagerService.dossierOpennedBySomeoneElse(dossierFetched)
        ) {
          foundErrors.push({
            errorType: DossierLightUpdateErrorType.CurrentlyInUse,
          });
        }

        // Check if we drag&drop from a PAD column to a technician column and if the allocation of resources is ambiguous
        if (
          dossier.appointment?.assignedResources?.length > 1 &&
          customDossierInfos?.oldResource?.type !== 'TECHNICIAN' &&
          customDossierInfos?.newResource?.type === 'TECHNICIAN'
        ) {
          foundErrors.push({
            errorType: DossierLightUpdateErrorType.AmbiguousTechAssign,
          });
        }

        if (dossier?.id) {
          // Check if a similar appointment already exists on the same period
          if (this.planningSb.isAppointmentAlreadyExist(dossier)) {
            foundErrors.push({
              errorType: DossierLightUpdateErrorType.ApmtAlreadyExists,
            });
          }

          // Check if the dossier is put in the outside column without an address
          if (
            dossier.appointment.outsideIntervention &&
            !dossier.appointment.interventionLocation
          ) {
            foundErrors.push({
              errorType: DossierLightUpdateErrorType.MissingAddrForMobility,
            });
          }

          const isDossierMoved = !this.resManagerService.isAnyRVRes(
            customDossierInfos.oldResource?.type ||
              customDossierInfos.newResource?.type
          );

          const dossierFitter = fitters?.find(
            (f) => f.id === dossier.appointment.fitterId
          );
          const fitterTimezone =
            this.timezoneService.getFitterTimezone(dossierFitter);

          const usedInterventionVehicle =
            dossierFitter.interventionVehicles?.find(
              (v) =>
                v.name === dossier?.appointment?.assignedInterventionVehicle
            );
          const usedTechnicians = dossierFitter.resources?.filter((tech) =>
            dossier?.appointment?.assignedResources?.includes(tech.name)
          );

          let fallbackRVUsePeriod: {
            start: string | Date;
            end: string | Date;
          } = undefined;
          if (isDossierMoved) {
            if (
              !dossier.appointment?.assignedReplacementVehicleUse?.start &&
              !dossier.appointment?.assignedReplacementVehicleUse?.end
            ) {
              fallbackRVUsePeriod = {
                start: dossier.appointment?.time,
                end: dossier.appointment?.endTime,
              };
            } else {
              fallbackRVUsePeriod = {
                start: dossierBeforeMove.appointment?.time,
                end: dossierBeforeMove.appointment?.endTime,
              };
            }
          }

          const movingDossierRVUsePeriod = getPeriodReplacementVehicleUse(
            dossier,
            fallbackRVUsePeriod
          );

          // The new bounds of the appointment we are trying to move
          const dossierStartTime = dayjs(dossier.appointment?.time).tz(
            fitterTimezone
          );
          const dossierEndTime = dayjs(dossier.appointment?.endTime).tz(
            fitterTimezone
          );

          // The bounds of the replacement vehicle use period
          const dossierRVUseStartTime = dayjs(
            movingDossierRVUsePeriod?.start
          ).tz(fitterTimezone);
          const dossierRVUseEndTime = dayjs(movingDossierRVUsePeriod?.end).tz(
            fitterTimezone
          );

          // If the appointment is moved outside of the replacement vehicle use period and this period was defined, then block the update
          // We test if one still contains the other, if not then it has been moved too far, independently of what event we moved and what rv use period was set (or not set) to
          const apmtIsBetweenRVUse =
            dossierStartTime.isBetween(
              dossierRVUseStartTime,
              dossierRVUseEndTime,
              'minutes',
              '[]'
            ) &&
            dossierEndTime.isBetween(
              dossierRVUseStartTime,
              dossierRVUseEndTime,
              'minutes',
              '[]'
            );
          const rvUseIsBetweenApmt =
            dossierRVUseStartTime.isBetween(
              dossierStartTime,
              dossierEndTime,
              'minutes',
              '[]'
            ) &&
            dossierRVUseEndTime.isBetween(
              dossierStartTime,
              dossierEndTime,
              'minutes',
              '[]'
            );

          if (!apmtIsBetweenRVUse && !rvUseIsBetweenApmt) {
            foundErrors.push({
              errorType: DossierLightUpdateErrorType.RVUseMismatchApmtTimes,
            });
          }

          // NOTE - There might be some other checks on the dossier to define here

          const foundBlockingRV =
            this.planningSb.isReplacementVehicleAlreadyUsed(
              dossier,
              dossier?.appointment?.assignedReplacementVehicle
            );

          if (foundBlockingRV) {
            foundErrors.push({
              errorType:
                DossierLightUpdateErrorType.ReplacementVehicleUnavailable,
            });
          }

          const foundBlockingIV =
            this.planningSb.isInterventionVehicleUnavailable(
              dossier,
              usedInterventionVehicle
            );

          if (foundBlockingIV) {
            foundErrors.push({
              errorType:
                DossierLightUpdateErrorType.InterventionVehicleUnavailable,
            });
          }

          usedTechnicians?.forEach((usedTech) => {
            const foundBlockingTech = this.planningSb.isTechnicianUnavailable(
              dossier,
              usedTech
            );

            if (foundBlockingTech) {
              foundErrors.push({
                errorType: DossierLightUpdateErrorType.TechnicianUnavailable,
                displayInterpolationParams: {
                  techName: usedTech.name,
                },
              });
            }
          });
        }

        if (foundErrors.length) {
          return {
            canUpdate: false,
            errors: foundErrors,
          };
        } else {
          return {
            canUpdate: true,
          };
        }
      })
    );
  }

  openAppointmentFormReduced() {
    this.planningPanelsStatesBeforeFormOpened = {
      ...this.planningPanelsStates,
    };
    this.planningPanelsStates = {
      isAppointmentFormOpened: true,
      isAppointmentFormReduced: true,
      isCustomEventFormOpened: false,
      isRightSideNavOpened: false,
      isLeftSideNavOpened: false,
      formErrors: null,
      mustSaveToPrint: false,
    };
    this.changeDetectorRef.markForCheck();

    this.triggerCalendarRerender();
  }

  showAppointmentAlreadyExist() {
    this.snackbarService.showError(
      this.translator.getInstantTranslation(
        'PlanningForm.appointmentAlreadyExist.title'
      ),
      this.translator.getInstantTranslation(
        'PlanningForm.appointmentAlreadyExist.massTexte'
      )
    );
  }

  showAppointmentIncompleteForm() {
    this.snackbarService.showError(
      this.translator.getInstantTranslation(
        'PlanningForm.incompleteDossier.title'
      ),
      this.translator.getInstantTranslation(
        'PlanningForm.incompleteDossier.massTexte'
      )
    );
  }

  onSaveDraft($event) {
    this.loadingService.setLoading(true);
    this.planningSb.saveDraftDossier($event).subscribe((dossier) => {
      this.planningCellSelected = dossier;
      this.planningSb.updatePlanningDossierChange(dossier as Dossier);
      this.changeDetectorRef.markForCheck();
      this.loadingService.setLoading(false);
    });
  }

  onSaveChatComments(comments: DossierComments[]) {
    this.loadingService.setLoading(true);
    this.planningSb
      .saveChatComments(this.planningCellSelected?.id, comments)
      .subscribe((dossier) => {
        this.planningCellSelected = dossier;
        this.planningSb.updatePlanningDossierChange(dossier as Dossier);
        this.changeDetectorRef.markForCheck();
        this.loadingService.setLoading(false);
      });
  }
}
